import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import AuthSplitLayout from '../components/auth/AuthSplitLayout';
import { useAuth } from '../context/AuthContext';
import { formatAuthError } from '../utils/authErrors';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await register(name.trim(), email.trim(), password, passwordConfirmation);
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const footerRow = (
    <div className="auth-split-footer-row auth-split-footer-row--end">
      <p className="auth-split-register-line">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </div>
  );

  return (
    <AuthSplitLayout
      title="Create account"
      subtitle="Set up your Polyrion credentials to get started."
      footerRow={footerRow}
    >
      <form className="auth-split-form" onSubmit={handleSubmit} noValidate>
        <label className="auth-split-field">
          <span className="auth-split-label">
            Name <abbr title="required">*</abbr>
          </span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>

        <label className="auth-split-field">
          <span className="auth-split-label">
            Email <abbr title="required">*</abbr>
          </span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <label className="auth-split-field">
          <span className="auth-split-label">
            Password <abbr title="required">*</abbr>
          </span>
          <span className="auth-split-input-wrap">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <button
              type="button"
              className="auth-split-input-toggle"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
            </button>
          </span>
        </label>

        <label className="auth-split-field">
          <span className="auth-split-label">
            Confirm password <abbr title="required">*</abbr>
          </span>
          <span className="auth-split-input-wrap">
            <input
              type={showConfirm ? 'text' : 'password'}
              name="password_confirmation"
              autoComplete="new-password"
              placeholder="Repeat password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={8}
            />
            <button
              type="button"
              className="auth-split-input-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              tabIndex={-1}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
            </button>
          </span>
        </label>

        {error ? <p className="auth-split-error" role="alert">{error}</p> : null}

        <button type="submit" className="auth-split-submit" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthSplitLayout>
  );
}
